import { KeyValuePairs, KeyValueStoreAPI } from '../api/KeyValueStoreAPI';
import DB from '../db/DB';
import { ReplicantError } from '../Errors';
import { sortBy } from '../utils/ArrayUtils';
import { duration } from '../utils/Utils';

type CacheOptions = {
    /** @default 5 minutes. */
    expirationMs: number;

    /** @default 1_000 */
    maxItemCount: number;
};

const DEFAULT_CACHE_OPTS: CacheOptions = {
    expirationMs: duration({ minutes: 5 }),
    maxItemCount: 1_000,
};

export default class ServerReplicantInternalKeyValueStore implements KeyValueStoreAPI {
    private cache: { [key: string]: { value: string; expiresAt: number; lastReadAt: number } } = {};

    constructor(
        private opts: {
            cacheOptions?: Partial<CacheOptions>;
            dateNow: () => number;
            db: DB<any>;
        },
    ) {}

    async getBatch(keys: string[]): Promise<KeyValuePairs> {
        const values = await Promise.all(keys.map((key) => this.getWithCaching(key)));
        const result: KeyValuePairs = {};

        keys.forEach((key, i) => {
            const value = values[i];
            if (value !== null && value !== undefined) {
                result[key] = value;
            }
        });

        return result;
    }

    async get(key: string): Promise<string | null> {
        return (await this.getBatch([key]))[key] || null;
    }

    async sendBatch(pairs: KeyValuePairs): Promise<void> {
        for (const key in pairs) {
            if (!pairs[key] || typeof pairs[key] !== 'string') {
                throw new ReplicantError(
                    `Invalid value for key: '${key}'.`,
                    'replication_error',
                    'key_value_storage_error',
                );
            }
        }

        await Promise.all(Object.entries(pairs).map(([key, value]) => this.opts.db.internalKvStore.write(key, value)));
    }

    async send(key: string, value: string): Promise<void> {
        await this.sendBatch({ [key]: value });
    }

    private async getWithCaching(key: string): Promise<string | null> {
        // Return cached item in case of cache hit:
        const cachedItem = this.readFromCache(key);

        if (cachedItem) {
            return cachedItem;
        }

        // Read from DB and populate cache in case of cache miss:
        const value = await this.opts.db.internalKvStore.fetch(key);

        if (value !== null) {
            this.writeToCache(key, value);
        }

        return value;
    }

    private readFromCache(key: string): string | void {
        const now = this.opts.dateNow();

        const cachedItem = this.cache[key];

        const isCacheHit = cachedItem && cachedItem.expiresAt > now;

        if (isCacheHit) {
            cachedItem.lastReadAt = now;

            return cachedItem.value;
        }
    }

    private writeToCache(key: string, value: string): void {
        const now = this.opts.dateNow();

        const expirationMs = this.opts.cacheOptions?.expirationMs ?? DEFAULT_CACHE_OPTS.expirationMs;

        this.cache[key] = { value, expiresAt: now + expirationMs, lastReadAt: now };

        this.pruneCache();
    }

    private pruneCache(): void {
        const maxItemCount = this.opts.cacheOptions?.maxItemCount ?? DEFAULT_CACHE_OPTS.maxItemCount;

        const itemsToPruneCount = Math.max(0, Object.keys(this.cache).length - maxItemCount);
        if (itemsToPruneCount === 0) {
            return;
        }

        const leastRecentlyUsedItems = sortBy(Object.entries(this.cache), ([_key, { lastReadAt }]) => lastReadAt);

        for (let i = 0; i < itemsToPruneCount; i++) {
            const keyToPrune = leastRecentlyUsedItems[i]![0];

            delete this.cache[keyToPrune];
        }
    }
}
